import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Check, CreditCard, ExternalLink, FileText, Loader2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { toast } from 'sonner';
import api, { apiError } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const fmtEUR = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const STATUS_STYLES = {
  trialing:  { c: 'bg-amber-500/15 text-amber-300 border-amber-500/30',   l: 'Essai' },
  active:    { c: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', l: 'Actif' },
  past_due:  { c: 'bg-rose-500/15 text-rose-300 border-rose-500/30',       l: 'Impayé' },
  canceled:  { c: 'bg-slate-500/15 text-slate-300 border-slate-500/30',    l: 'Annulé' },
  suspended: { c: 'bg-rose-600/20 text-rose-200 border-rose-500/40',       l: 'Suspendu' },
};

export default function Subscription() {
  const { agency } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [busyPlan, setBusyPlan] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [interval, setInterval] = useState('month');

  useEffect(() => {
    let active = true;
    api.get('/stripe/catalog').then(({ data }) => { if (active) setCatalog(data); }).catch(() => {});
    api.get('/billing/invoices').then(({ data }) => { if (active) setInvoices(data); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const startCheckout = async (planKey) => {
    setBusyPlan(planKey);
    api.post('/stripe/checkout', { plan: planKey, origin: window.location.origin })
      .then(({ data }) => { window.location.href = data.url; })
      .catch((e) => {
        toast.error(apiError(e, "Impossible de démarrer le paiement"));
        setBusyPlan(null);
      });
  };

  const openPortal = () => {
    setPortalBusy(true);
    api.post('/stripe/portal', { origin: window.location.origin })
      .then(({ data }) => { window.location.href = data.url; })
      .catch((e) => {
        toast.error(apiError(e, "Impossible d'ouvrir le portail"));
        setPortalBusy(false);
      });
  };

  const currentStatus = (agency?.subscription_status || 'trialing').toLowerCase();
  const statusStyle = STATUS_STYLES[currentStatus] || STATUS_STYLES.active;
  const isActive = ['active', 'trialing', 'past_due'].includes(currentStatus);
  const currentPlanName = agency?.plan || '';
  const periodEnd = agency?.current_period_end || agency?.renews_at;

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto fade-in" data-testid="subscription-page">
      <PageHeader
        title="Abonnement"
        subtitle="Gère ton offre ChatHub"
        badge={<Badge variant="outline" className={statusStyle.c}>{statusStyle.l}</Badge>}
      />

      {/* Current state card */}
      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-indigo-500/[0.04] to-transparent p-5 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="current-plan-card">
        <div>
          <div className="text-xs text-slate-500 mb-0.5">Ton offre</div>
          <div className="font-display text-2xl font-bold text-white flex items-center gap-2">
            {currentPlanName || '—'}
            <Badge variant="outline" className={statusStyle.c}>{statusStyle.l}</Badge>
          </div>
          {periodEnd && (
            <div className="text-xs text-slate-400 mt-1">
              {currentStatus === 'trialing' ? "Fin de l'essai" : (agency?.cancel_at_period_end ? 'Annulation' : 'Renouvellement')} le {new Date(periodEnd).toLocaleDateString('fr-FR')}
            </div>
          )}
        </div>
        <Button onClick={openPortal} disabled={portalBusy || !agency?.stripe_customer_id} data-testid="open-portal" className="bg-white/5 hover:bg-white/10 text-white border border-white/10">
          {portalBusy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <ExternalLink size={14} className="mr-1.5" />}
          {agency?.stripe_customer_id ? 'Gérer la facturation & les factures' : 'Aucun historique de facturation'}
        </Button>
      </div>

      {/* Billing interval toggle */}
      <div className="flex items-center justify-center mb-5" data-testid="billing-interval-toggle">
        <div className="inline-flex rounded-xl border border-white/10 bg-white/[0.03] p-1">
          <button onClick={() => setInterval('month')} data-testid="interval-month"
            className={`px-4 py-1.5 text-sm rounded-lg transition-all ${interval === 'month' ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/30' : 'text-slate-400 hover:text-white'}`}>
            Mensuel
          </button>
          <button onClick={() => setInterval('year')} data-testid="interval-year"
            className={`px-4 py-1.5 text-sm rounded-lg transition-all flex items-center gap-1.5 ${interval === 'year' ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/30' : 'text-slate-400 hover:text-white'}`}>
            Annuel <span className="text-[10px] bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded-full">−20%</span>
          </button>
        </div>
      </div>

      {/* Plans */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {(catalog?.plans || []).filter(p => p.interval === interval).map(p => {
          const monthlyName = p.interval === 'year' ? p.name.replace(/ \(yearly\)$/i, '') : p.name;
          const isCurrent = currentPlanName === monthlyName && isActive;
          const popular = (p.monthly_key || p.key) === 'starter';
          const displayPrice = p.interval === 'year' ? p.price_eur / 12 : p.price_eur;
          return (
            <div key={p.key} className={`relative rounded-2xl border p-6 transition-all hover:-translate-y-0.5 ${popular ? 'border-indigo-500/50 bg-indigo-500/[0.06]' : 'border-white/10 bg-white/[0.02]'}`} data-testid={`plan-card-${p.key}`}>
              {popular && <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-indigo-500 text-white border-0">Le plus populaire</Badge>}
              <div className="text-base font-semibold text-white mb-1">{monthlyName}</div>
              
              {p.interval === 'year' && <div className="text-[10px] text-emerald-400 font-bold uppercase tracking-wider mb-1">−20% vs mensuel</div>}
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-bold text-white font-display">{fmtEUR(displayPrice)}</span>
                <span className="text-xs text-slate-500">/mois</span>
              </div>
              {p.interval === 'year' && <div className="text-[11px] text-slate-500 mb-3">{fmtEUR(p.price_eur)} facturé par an</div>}
              <ul className="space-y-2 text-sm text-slate-300 mb-6 min-h-[100px] mt-3">
                <li className="flex items-center gap-2"><Check size={13} className="text-emerald-400" /> Toutes les fonctionnalités incluses</li>
                <li className="flex items-center gap-2"><Check size={13} className="text-emerald-400" /> Intégration DropFans</li>
                <li className="flex items-center gap-2"><Check size={13} className="text-emerald-400" /> Annulable à tout moment</li>
              </ul>
              {isCurrent ? (
                <Button disabled className="w-full bg-white/10 text-slate-300" data-testid={`current-plan-${p.key}`}>Offre actuelle</Button>
              ) : (
                <Button
                  className={`w-full ${popular ? 'bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 shadow-lg shadow-indigo-500/30' : 'bg-gradient-to-r from-indigo-500/80 to-violet-500/80 hover:from-indigo-500 hover:to-violet-500'} text-white font-semibold`}
                  onClick={() => startCheckout(p.key)}
                  disabled={!!busyPlan}
                  data-testid={`checkout-${p.key}`}
                >
                  {busyPlan === p.key ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CreditCard size={14} className="mr-1.5" />}
                  {isActive ? "Passer à l'offre" : "S'abonner"}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Invoices */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="font-display text-lg font-bold text-white">Factures</h3>
          <span className="text-xs text-slate-500">({invoices.length})</span>
        </div>
        {invoices.length === 0 ? (
          <div className="rounded-xl border border-white/5 bg-white/[0.02] p-6 text-center text-sm text-slate-500" data-testid="no-invoices">
            Aucune facture pour l'instant — ta première facture apparaîtra ici après ton premier mois payé.
          </div>
        ) : (
          <div className="rounded-xl border border-white/10 bg-[#08080A] divide-y divide-white/5" data-testid="invoices-list">
            {invoices.map((inv) => (
              <div key={inv.stripe_invoice_id} className="px-4 py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="text-white font-medium">{fmtEUR(inv.amount_paid)} <span className="text-xs text-slate-500 uppercase">{inv.currency}</span></div>
                  <div className="text-xs text-slate-500">{inv.paid_at ? new Date(inv.paid_at).toLocaleString('fr-FR') : '—'}</div>
                </div>
                <div className="flex gap-2">
                  {inv.pdf_url && <a href={inv.pdf_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-300 hover:text-indigo-200 flex items-center gap-1"><FileText size={12} /> PDF</a>}
                  {inv.hosted_url && <a href={inv.hosted_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-300 hover:text-indigo-200 flex items-center gap-1"><ExternalLink size={12} /> Voir</a>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
